import type { RuleSet } from 'styled-components';

import styled, { css } from 'styled-components';

import { Typography } from '../../shared/core/typography/Typography';
import { FesLink } from '~/components/shared/core/link/FesLink';
import { SiteType } from '~/utilities/graphql/codegen';

const GstarAccordionElementColorStyle = css`
  color: ${({ theme }) => theme.colors.content.secondary};
`;

const AafAccordionElementColorStyle = css`
  color: ${({ theme }) => theme.colors.content.primary};
`;

const GstarAccordionLinkColorStyle = css`
  color: ${({ theme }) => theme.colors.content.hyperlink};
  font-size: ${({ theme }) => theme.font.size[3]};
`;

const WahtsAccordionLinkColorStyle = css`
  color: ${({ theme }) => theme.colors.content.primary};
  font-size: ${({ theme }) => theme.font.size[3]};
`;

const AafAccordionLinkColorStyle = css`
  color: ${({ theme }) => theme.colors.content.primary};
`;

export const AccordionElementColorConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarAccordionElementColorStyle,
  [SiteType.Wahts]: GstarAccordionElementColorStyle,
  [SiteType.Outlet]: GstarAccordionElementColorStyle,
  [SiteType.Employeeshop]: GstarAccordionElementColorStyle,
  [SiteType.Aaf]: AafAccordionElementColorStyle,
};

export const AccordionLinkColorConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GstarAccordionLinkColorStyle,
  [SiteType.Wahts]: WahtsAccordionLinkColorStyle,
  [SiteType.Outlet]: GstarAccordionLinkColorStyle,
  [SiteType.Employeeshop]: GstarAccordionLinkColorStyle,
  [SiteType.Aaf]: AafAccordionLinkColorStyle,
};

export const S = {
  AccordionTitle: styled(Typography)`
    padding-bottom: 9px;
    color: ${({ theme }) => theme.colors.content.primary};
    font-size: ${({ theme }) => theme.font.size[3]};
  `,

  Container: styled.div`
    padding: ${({ theme }) => theme.padding['2xs']} 0 ${({ theme }) => theme.padding.sm};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};

    &:last-child {
      border-bottom: none;
    }
  `,

  Header: styled(Typography)`
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.font.size[3]};
    text-transform: capitalize;
    gap: ${({ theme }) => theme.spacing.y.xs};

    ${({ theme }) => AccordionElementColorConfig[theme.siteType]};

    svg {
      flex: none;
      width: 32px;
      height: 32px;

      /* stylelint-disable-next-line a11y/no-outline-none */
      &:focus:not(:focus-visible) {
        outline: none;
      }
    }
  `,
  Layout: styled.div<{ $size?: 'default' | 'small' }>`
    ${({ theme }) => css`
      padding: ${theme.padding['2xs']} 32px 0 ${({ theme }) => theme.padding.lg};
      font-size: ${theme.font.size[3]};
      line-height: 1.4;

      ${AccordionElementColorConfig[theme.siteType]};

      a {
        text-decoration: underline;
        font-size: ${theme.font.size[3]};

        ${AccordionLinkColorConfig[theme.siteType]};
      }
    `}
  `,
  PreferredMaterials: {
    Layout: styled.div`
      padding: ${({ theme }) => theme.padding['2xs']} 32px 0 ${({ theme }) => theme.padding.lg};
    `,
    Link: styled(FesLink)`
      text-decoration: underline;
      font-size: ${({ theme }) => theme.font.size[3]};

      ${({ theme }) => AccordionLinkColorConfig[theme.siteType]};
    `,
  },

  Fibers: {
    FiberList: styled.ul`
      margin: 0;
      padding: 0;
      list-style: none;
    `,
    Fiber: styled.li`
      ${({ theme }) => css`
        padding-bottom: ${theme.padding['2xs']};
        font-size: ${theme.font.size[3]};
        line-height: 1.4;
        font-weight: ${theme.variables.font.weight.regular};

        ${AccordionElementColorConfig[theme.siteType]};

        &.fiber-name {
          font-weight: ${theme.variables.font.weight.medium};
        }

        a {
          text-decoration: underline;
          font-size: ${theme.font.size[3]};

          ${AccordionLinkColorConfig[theme.siteType]};
        }
      `}
    `,
  },

  FactoryList: {
    Layout: styled.div`
      ${({ theme }) => css`
        padding: ${theme.padding['2xs']} 20px 0 ${({ theme }) => theme.padding.lg};

        a {
          text-decoration: underline;
          font-size: ${theme.font.size[3]};

          ${AccordionLinkColorConfig[theme.siteType]};
        }
      `}
    `,
    FactoryList: styled.dl`
      margin: 0;
    `,
    Process: styled.dt`
      ${({ theme }) => css`
        margin-bottom: ${theme.spacing.y['2xs']};
        font-size: ${theme.font.size[3]};
        line-height: 16px;
        font-weight: ${theme.variables.font.weight.medium};

        ${AccordionElementColorConfig[theme.siteType]};
      `}
    `,
    Factory: styled.dd`
      margin: 0;
      margin-bottom: ${({ theme }) => theme.spacing.y.md};
      font-size: ${({ theme }) => theme.font.size[2]};
      line-height: 18px;

      ${({ theme }) => AccordionElementColorConfig[theme.siteType]};

      &:last-child {
        margin-bottom: 0;
      }
    `,
    IconWrapper: styled.span`
      display: inline-block;
      width: 12px;
      height: 16px;
      margin-top: 2px;
      margin-right: ${({ theme }) => theme.spacing.y['2xs']};
      vertical-align: top;
      color: ${({ theme }) => theme.colors.blue['800']};
    `,
  },
};
